<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import whatsappCallingClient from 'dashboard/helper/whatsappCallingClient';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  inboxId: { type: [String, Number], default: null },
  label: { type: String, default: 'Call' },
  icon: { type: [String, Object, Function], default: 'i-ri-phone-line' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
  variant: { type: String, default: 'secondary' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const dialogRef = ref(null);
const isInitiatingCall = ref(false);

const inboxesList = useMapGetter('inboxes/getInboxes');

// Filter WhatsApp Cloud inboxes that have calling enabled
const whatsappCallingInboxes = computed(() =>
  (inboxesList.value || []).filter(inbox => {
    const isWhatsApp = inbox.channel_type === INBOX_TYPES.WHATSAPP_CLOUD;
    const hasCallingEnabled =
      inbox.calling_enabled === true ||
      inbox.provider_config?.calling_enabled === true;
    return isWhatsApp && hasCallingEnabled && !inbox.disabled;
  })
);

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const initiateCall = async inboxId => {
  if (isInitiatingCall.value) return;

  isInitiatingCall.value = true;
  try {
    const response = await whatsappCallingClient.initiateOutboundCall({
      inboxId,
      contactId: props.contactId,
      to: props.phone,
    });

    const conversationId = response?.conversation_id || response?.id;

    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));

    // Navigate to conversation if we got one
    if (conversationId) {
      navigateToConversation(conversationId);
    }
  } catch (error) {
    const apiError = error?.response?.data?.error || error?.message;
    useAlert(apiError || t('CONTACT_PANEL.CALL_FAILED'), 'danger');
  } finally {
    isInitiatingCall.value = false;
  }
};

const onClick = async () => {
  // If user provided specific inbox, use it directly
  if (props.inboxId) {
    await initiateCall(props.inboxId);
    return;
  }

  // If multiple inboxes, let user pick one
  if (whatsappCallingInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }

  // Single inbox, call it directly
  const [inbox] = whatsappCallingInboxes.value;
  if (inbox) {
    await initiateCall(inbox.id);
  }
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await initiateCall(inbox.id);
};
</script>

<template v-if="whatsappCallingInboxes.length > 0 && phone">
  <div class="contents">
    <!-- WhatsApp Calling Button -->
    <Button
      v-tooltip.top-end="tooltipLabel || $t('CONTACT_PANEL.CALL')"
      v-bind="attrs"
      :disabled="isInitiatingCall"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      :variant="variant"
      class="text-emerald-600 hover:text-emerald-700"
      @click="onClick"
    />

    <!-- Inbox Picker Dialog (if multiple calling-enabled inboxes) -->
    <Dialog
      v-if="whatsappCallingInboxes.length > 1"
      ref="dialogRef"
      :title="t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in whatsappCallingInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-phone-fill text-emerald-600" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </div>
</template>
